import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserInfo } from '../../../../models/user-info.model';
import { UserListfields } from '../../../../models/user-list-fields.model';
import { GraphService } from 'src/app/services/graph/graph.service';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { MatSelectChange } from '@angular/material/select';
import { CustomValidators } from 'src/app/utils/validators';
import { OrgsService } from 'src/app/services/orgs/orgs.service';
import { OrgModel } from 'src/app/models/org.model';

@Component({
  selector: 'app-create-dialog',
  templateUrl: './create-dialog.component.html',
  styleUrls: ['./create-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CreateDialogComponent {
  selectedEtablissement: string;
  currentUser: UserInfo = new UserInfo();
  errorUpdate = false;
  isErrorUpdate = false;
  isBusy = false;
  isBusyLoadingRole = false;
  newUser: UserListfields = new UserListfields();
  error: Error = { error: { message: '', code: '' } };
  errorUpdatePwd: Error = { error: { message: '', code: '' } };
  errorGettingRole = false;
  phoneValidator = '^[0-9]*$';
  avsValidator = '[0-9]{3}\\.[0-9]{4}\\.[0-9]{4}\\.[0-9]{2}';
  dateOfBirthValidation =
    '^([0]?[1-9]|[1|2][0-9]|[3][0|1])[.]([0]?[1-9]|[1][0-2])[.]([0-9]{4}|[0-9]{2})$';
  NameNotNumberValidation = '^([^0-9]*)$';

  maxlength = 30;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  preferredLanguage = [
    {
      id: 1,
      name: 'Français',
      acronym: 'fr',
      lang: 'fr',
    },
    {
      id: 2,
      name: 'Deutsch',
      acronym: 'de',
      lang: 'de',
    },
  ];

  defaultDial = '+41';

  // Pwd
  addUserForm: FormGroup;
  isUserCreated = false;

  // Role
  availableRoles: OrgModel[];

  constructor(
    public matDialogRef: MatDialogRef<CreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) _data: { action: string; currentUser: UserInfo },
    private _formBuilder: FormBuilder,
    private _orgService: OrgsService,
    private _graphService: GraphService
  ) {
    this.currentUser = _data.currentUser;

    this.selectedEtablissement = this.currentUser.groupEtablissement[0];
    this.addUserForm = this._formBuilder.group({
      avs: ['', [Validators.required, Validators.pattern(this.avsValidator)]],
      dateOfBirth: [
        '',
        [Validators.required, Validators.pattern(this.dateOfBirthValidation)],
      ],
      gender: ['', Validators.required],
      firstName: ['', Validators.pattern(this.NameNotNumberValidation)],
      lastName: ['', Validators.pattern(this.NameNotNumberValidation)],
      defaultDial: [''],
      title: ['', Validators.required],
      phone: [
        '',
        [
          Validators.pattern(this.phoneValidator),
          Validators.minLength(9),
          Validators.required,
        ],
      ],
      email: [
        '',
        [
          Validators.required,
          CustomValidators.validEmail(),
          CustomValidators.EmailInterdiction(),
        ],
      ],
      preferredLanguage: ['', Validators.required],
      address: ['', [Validators.minLength(5), Validators.maxLength(50)]],
      zip: [
        '',
        [
          Validators.pattern('^[0-9]*$'),
          Validators.minLength(4),
          Validators.maxLength(10),
        ],
      ], // Must only be number
      locality: [
        '',
        [Validators.pattern('^([^0-9]*)$'), Validators.maxLength(30)],
      ],
      grpEtablissement: ['', Validators.required],
      roles: [''],
    });

    this.addUserForm.get('defaultDial').setValue('+41');
    this.addUserForm
      .get('grpEtablissement')
      .setValue(this.selectedEtablissement);
    this.initForm();
  }

  fetchCreateUserData(): void {
    const user: UserListfields = new UserListfields();

    user.avs = this.addUserForm.get('avs').value;
    user.birthday = this.addUserForm.get('dateOfBirth').value;
    user.gender = this.addUserForm.get('gender').value;
    user.firstname = this.addUserForm.get('firstName').value.trim();
    user.lastname = this.addUserForm.get('lastName').value.trim();
    user.language = this.addUserForm.get('preferredLanguage').value.acronym;
    user.recoveryEmail = this.addUserForm.get('email').value;
    user.mobilephone =
      this.addUserForm.get('defaultDial').value +
      this.addUserForm.get('phone').value;
    user.canton = 'VS';

    if (this.addUserForm.get('title').value != '') {
      user.usertitle = this.addUserForm.get('title').value.trim();
    }
    if (this.addUserForm.get('address').value != '') {
      user.address = this.addUserForm.get('address').value;
    }
    if (this.addUserForm.get('zip').value != '') {
      user.npa = this.addUserForm.get('zip').value;
    }
    if (this.addUserForm.get('locality').value != '') {
      user.locality = this.addUserForm.get('locality').value;
    }
    if (this.addUserForm.get('grpEtablissement').value != '') {
      user.SettlementGroup = this.addUserForm.get('grpEtablissement').value;
    }
    if (this.addUserForm.get('phone').value != '') {
      user.mobilephone =
        this.addUserForm.get('defaultDial').value +
        this.addUserForm.get('phone').value;
    }
    if (this.addUserForm.get('email').value != '') {
      user.recoveryEmail = this.addUserForm.get('email').value;
    }
    if (this.addUserForm.get('roles').value != null) {
      user.roles = this.addUserForm.get('roles').value;
    }

    user.type = 'User';
    user.Title = 'Demande nouvel utilisateur';

    this.newUser = user;
    // Reset all errors and boolean in case of multiple error
    this.isBusy = false;
    this.isUserCreated = false;
    this.errorUpdate = false;
  }

  async initForm() {
    await this.getRolesForUser(this.selectedEtablissement);
  }

  async getRolesForUser(grpEtablissement: string) {
    try {
      this.addUserForm.get('grpEtablissement').disable();
      this.addUserForm.get('roles').disable();

      this.availableRoles = [];
      this.isBusyLoadingRole = true;
      this.errorGettingRole = false;

      this._orgService.getMyAssignableRole(grpEtablissement).subscribe({
        next: roles => {
          if (roles) {
            this.availableRoles = roles;
          }
        },
        error: () => (this.errorGettingRole = true),
      });
    } catch {
      this.errorGettingRole = true;
      this.isBusyLoadingRole = false;
    } finally {
      this.isBusyLoadingRole = false;
      this.addUserForm.get('grpEtablissement').enable();
      this.addUserForm.get('roles').enable();
    }
  }

  async onChangeEtablissement($event: MatSelectChange) {
    await this.getRolesForUser($event.value);
  }

  async CreateUser(): Promise<void> {
    this.errorUpdatePwd = { error: { message: '', code: '' } };
    this.isErrorUpdate = false;
    this.isUserCreated = false;

    try {
      this.fetchCreateUserData();
      this.isBusy = true;
      this._graphService.addUserDemand(this.newUser).subscribe({
        next: () => (this.isUserCreated = true),
        error: error => {
          this.isErrorUpdate = true;
          let customError: Error = { error: undefined };

          if (error.statusText == 'Unknown Error') {
            customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
          } else {
            if (error.error) {
              customError = error.error;
            }
            if (error.error.errors) {
              customError = {
                error: { message: 'Unknown Error', code: 'ERR2' },
              };
            } else {
              if (error.error) {
                customError = error.error;
              }
            }
          }

          if (customError.error === undefined) {
            customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
          }
          this.error = customError;
        },
        complete: () => (this.isBusy = false),
      });
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (error.error.errors) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (error.error) {
            customError = error.error;
          }
        }
      }

      if (customError.error === undefined) {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      }
      this.isErrorUpdate = true;
      this.error = customError;
      this.isUserCreated = false;
    } finally {
      this.isBusy = false;
      // this.addUserForm.reset();
      // this.addUserForm.get('defaultDial').setValue('+41');
      // this.addUserForm
      //   .get('grpEtablissement')
      //   .setValue(this.selectedEtablissement);
    }
  }
}
